import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import {  Button, ListItemText, useTheme } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import PersonIcon from '../../icons/PersonIcon';
import BillingListIcon from '../../icons/BillingListIcon';
import QuestionMarkIcon from '../../icons/QuestionMarkIcon';
import CurrentCallIcon from '../../icons/CurrentCallIcon';
import { useAuth } from '../../hooks/useAuth';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { getInitialsOfName } from '../../utils';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

export default function AccountMenu() {
    const { getUserDetailsNotAsync, setUserDetailsNotAsync, refreshSessionData } = useAuth();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const navigate = useNavigate();
    const theme = useTheme();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigateToPage = (url) => () => {
        navigate(`${url}`);
    };

    const userDetails = () => {
        let userDetails = getUserDetailsNotAsync();
        return { name: userDetails?.contact?.name, email: userDetails?.contact?.email };
    };

    return (
        <React.Fragment>
            <Tooltip title="My Account">
                <Button
                    onClick={handleClick}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    endIcon={false ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    color="inherit"
                    sx={(theme) => ({
                        borderRadius: theme.typography.borderRadius.xxxLarge,

                        padding: '0.5rem',
                        textTransform: 'unset',

                        '&:hover': {
                            backgroundColor: theme.palette.black[100],
                        },
                    })}
                >
                    <Avatar
                        src={userDetails().profileImage || ''}
                        sx={{
                            width: '2rem',
                            height: '2rem',
                        }}
                    >
                        {getInitialsOfName(userDetails().name || '')}
                    </Avatar>
                </Button>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            width: 288,

                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },

                            '& .MuiButtonBase-root.MuiMenuItem-root': {
                                color: '#344054',
                                fontWeight: theme.typography.fontWeightRegular,
                                fontSize: theme.typography.customFontSize.small,
                                padding: '10px 16px',
                            },

                            '& .MuiButtonBase-root.MuiMenuItem-root .MuiListItemIcon-root': {
                                minWidth: 'unset',
                                marginRight: '0.8rem',
                            },

                            '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                                color: '#344054',
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem
                    disabled
                    sx={{
                        '&.Mui-disabled': {
                            opacity: 1,
                        },
                    }}
                >
                    <Avatar
                        src={userDetails().profileImage || ''}
                        sx={{
                            '&.MuiAvatar-root': {
                                // width: '2.5rem',
                                // height: '2.5rem',
                            },
                        }}
                    >
                        {getInitialsOfName(userDetails().name || '')}
                    </Avatar>
                    <ListItemText
                        primary={userDetails().name}
                        secondary={userDetails().email}
                        secondaryTypographyProps={{
                            fontSize: 'customFontSize.small',
                            fontWeight: 'fontWeightRegular',
                            lineHeight: '1.25rem',
                            color: 'grey.0',

                            textOverflow: 'ellipsis',

                            overflow: 'hidden',
                        }}
                        primaryTypographyProps={{
                            fontSize: 'customFontSize.small',
                            fontWeight: 'fontWeightMedium',
                            lineHeight: '1.25rem',
                            color: 'grey.1100',

                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                        }}
                    />
                </MenuItem>
                <Divider />

                <MenuItem component={Link} to={'/account-info'}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    Account Information
                </MenuItem>

                <MenuItem component={Link} to={'/payment-information/payment-methods'}>
                    <ListItemIcon>
                        <CreditCardIcon fontSize="small" />
                    </ListItemIcon>
                    Payment Methods
                </MenuItem>

                <MenuItem component={Link} to={'/payment-information/billing-history'}>
                    <ListItemIcon>
                        <BillingListIcon fontSize="small" />
                    </ListItemIcon>
                    Billing History
                </MenuItem>

                <Divider />

                <MenuItem component={Link} to={'/customer-support'}>
                    <ListItemIcon>
                        <QuestionMarkIcon fontSize="small" />
                    </ListItemIcon>
                    Customer Support
                </MenuItem>
                <MenuItem component={Link} to={'/contact-sales'}>
                    <ListItemIcon>
                        <CurrentCallIcon fontSize="small" />
                    </ListItemIcon>
                    Contact Sales
                </MenuItem>

                <MenuItem
                    component={Link}
                    to={'/signout'}
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/signout' + window.location.search, {
                            replace: true,
                        });
                    }}
                >
                    <ListItemIcon>
                        <PowerSettingsNewIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </React.Fragment>
    );
}
